import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import Handler from "../../Helpers/Handler";
import { textCapitalize } from "../../Helpers/Utils";

// Staff options are scoped to the task's own hub and to active employment
// status - TaskUtils.assignStaff still has the final say (task-type/role
// eligibility), this just keeps the dropdown from offering someone who
// obviously can't take it.
const TaskAssignModal = ({ taskId, hubId, currentStaffId, onClose, onSaved }) => {
    const modalRef = useRef(null);
    const bsModalRef = useRef(null);
    const [staffId, setStaffId] = useState("");
    const [staffOptions, setStaffOptions] = useState([]);
    const [loadingStaff, setLoadingStaff] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const el = modalRef.current;
        if (!el || !window.bootstrap) return undefined;
        const instance = window.bootstrap.Modal.getOrCreateInstance(el);
        bsModalRef.current = instance;
        const handleHidden = () => onClose?.();
        el.addEventListener("hidden.bs.modal", handleHidden);
        instance.show();
        return () => {
            el.removeEventListener("hidden.bs.modal", handleHidden);
            instance.hide();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        (async () => {
            setLoadingStaff(true);
            const response = await Handler({ method: "get", url: `/admin/task/${taskId}/eligible-staff` });
            setStaffOptions(response?.success ? (response?.data?.data || []).filter((s) => s.staffId !== currentStaffId) : []);
            setLoadingStaff(false);
        })();
    }, [taskId]);

    const onSubmit = async () => {
        if (!staffId) {
            toast.error("Select a staff member");
            return;
        }
        setSaving(true);
        const response = await Handler({ method: "patch", url: `/admin/task/assign/${taskId}`, data: { staffId } });
        if (response?.success) {
            toast.success(response?.data?.message || "Task assigned");
            onSaved?.();
        } else {
            toast.error(textCapitalize(response?.data?.message || "Unable to assign this task"));
        }
        setSaving(false);
    };  
    return (
        <div className="modal fade" tabIndex="-1" ref={modalRef} aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content border-0 rounded-4">
                    <div className="modal-header">
                        <h5 className="modal-title fw-semibold">{currentStaffId ? "Reassign Task" : "Assign Task"}</h5>
                        <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div className="modal-body">
                        <label className="form-label">Staff member</label>
                        <select className="form-select" value={staffId} onChange={(e) => setStaffId(e.target.value)} disabled={loadingStaff || saving}>
                            <option value="" disabled>{loadingStaff ? "Loading staff..." : "Select staff member"}</option>
                            {staffOptions.map((s) => (
                                <option key={s.staffId} value={s.staffId}>{s.name || ""} - {s.roleName || ""}</option>
                            ))}
                        </select>
                        {!loadingStaff && !staffOptions.length ? (
                            <div className="form-text text-danger">No available staff at this task's hub whose role allows this task type.</div>
                        ) : null}
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-outline-secondary" data-bs-dismiss="modal" disabled={saving}>Cancel</button>
                        <button type="button" className="btn btn-primary" onClick={onSubmit} disabled={saving}>
                            {saving ? "Assigning..." : "Assign"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TaskAssignModal;
